// src/app/modules/.../ServiceRequests.tsx
import React, {FC, useEffect, useMemo, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Button} from '../../../../components/Button/Button'
import {Loading} from '../../../../components/Loading/Loading'
import styles from './serviceRequests.module.css'
import {Input} from '../../../../components/Input/Input'
import useModals from '../../../../hooks/useModals'
import {GET, POST, PUT} from '../../../../services/api'
import SVG from 'react-inlinesvg'
import {EditorQuill} from '../../../../components/EditorQuill/EditorQuill'
import {i18n} from '../../../../translate/i18n'
import Swal from 'sweetalert2'
import {useNavigate, useParams} from 'react-router-dom'
import {IconPickerModal, GridSelectionModal, QuantityModal, AddComponentModal} from './components'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'

// DnD-kit
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import uuid from 'react-uuid'

// Catálogo dos "AllForms"
import {
  FORMS_CATALOG,
  FormBlockKind,
  FormBlockMeta,
} from '../../../../components/ServiceResquests/Forms'
import {set} from 'rsuite/esm/internals/utils/date'
import {typeUser} from '../../../Commons/Configuracoes/Usuarios/typeUser'

// ---------------- Tipos/Helpers ----------------
type Block = {id: string; kind: FormBlockKind; gridIds?: number[]; maxQuantity?: number}
const metaByKind = (k: FormBlockKind) => FORMS_CATALOG.find((m) => m.id === k)!

type GridHotel = {
  id: number
  title: string
  status: boolean
  startHour: string
  endHour: string
  interval: number
}

// Normaliza SVG vindo da API (remove width/height/fill/stroke inline)
const normalizeSvg = (svg: string) =>
  svg?.replace(/(width|height)="[^"]*"/gi, '')?.replace(/\s*(fill|stroke)="[^"]*"/gi, '') ?? ''

// Item sortável (bloco no canvas)
function SortableBlock({
  block,
  meta,
  onRemove,
  onDetails, // ADIÇÃO
  onOpenGridModal,
  onOpenQuantityModal,
}: {
  block: Block
  meta: FormBlockMeta
  onRemove: (id: string) => void
  onDetails: (id: string) => void
  onOpenGridModal: (blockId: string) => void
  onOpenQuantityModal: (blockId: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({id: block.id})

  return (
    <div
      ref={setNodeRef}
      className={`${styles.item} ${isDragging ? styles.dragging : ''}`}
      style={{transform: CSS.Transform.toString(transform), transition}}
      aria-roledescription='sortable item'
    >
      <div className={styles.header}>
        {/* HANDLE — só ele arrasta */}
        <button
          type='button'
          className={styles.handle}
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          aria-label={i18n.t('service_requests.drag_title')}
          title={i18n.t('service_requests.drag_action')}
        >
          <i className='bi bi-grip-vertical' />
        </button>

        {meta.icon && <i className={`${meta.icon}`} />}
        <span className={styles.title}>
          {meta.title}
          {block.gridIds && block.gridIds.length > 0 && (
            <span
              className='badge badge-primary ms-2'
              title={`${i18n.t('service_requests.grid_services_selected')}: ${block.gridIds.join(
                ', '
              )}`}
            >
              {block.gridIds.length}{' '}
              {block.gridIds.length > 1
                ? i18n.t('service_requests.grids_plural')
                : i18n.t('service_requests.grid')}
            </span>
          )}
          {block.maxQuantity && block.maxQuantity > 1 && (
            <span
              className='badge badge-success ms-2'
              title={`${i18n.t('service_requests.max_quantity_title')} ${block.maxQuantity}`}
            >
              {i18n.t('service_requests.max_prefix')} {block.maxQuantity}
            </span>
          )}
        </span>

        <span className={styles.spacer} />

        {/* Botão de Quantidade - aparece para "quantity", "selectListQuantity" e "selectCount" */}
        {(block.kind === 'quantity' ||
          block.kind === 'selectListQuantity' ||
          block.kind === 'selectCount') && (
          <button
            type='button'
            className={styles.iconBtn}
            onClick={() => onOpenQuantityModal(block.id)}
            title={i18n.t('service_requests.max_quantity_modal_title')}
            aria-label={i18n.t('service_requests.max_quantity_modal_title')}
          >
            <i className='bi bi-123' />
          </button>
        )}

        {/* Botão de Grades de Serviços - aparece apenas para tipos "select" */}
        {block.kind.startsWith('select') && (
          <button
            type='button'
            className={styles.iconBtn}
            onClick={() => onOpenGridModal(block.id)}
            title={i18n.t('service_requests.grid_services')}
            aria-label={i18n.t('service_requests.grid_services')}
          >
            <i className='bi bi-grid' />
          </button>
        )}

        <button
          type='button'
          className={styles.iconBtn}
          onClick={() => onRemove(block.id)}
          title={i18n.t('service_requests.remove_title')}
          aria-label={i18n.t('service_requests.remove_aria_label')}
        >
          <i className='bi bi-trash text-danger' />
        </button>
      </div>

      {/* Preview só visual e compacto */}
      <div className={styles.body}>{meta.getPreview()}</div>
    </div>
  )
}

// ---------------- Página ----------------
const Content: FC = () => {
  const navigate = useNavigate()
  const {id} = useParams<{id: string}>()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [openOptionsMenu, setOpenOptionsMenu] = useState(false)

  const [blocks, setBlocks] = useState<Block[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)

  // Campos da sessão (topo)
  const [sessionTitle, setSessionTitle] = useState<string>('') // título
  const [sessionIcon, setSessionIcon] = useState<string>('') // classe bi-* (fallback)
  const [sessionIconId, setSessionIconId] = useState<number | null>(null) // id do ícone escolhido
  const [sessionIconSvg, setSessionIconSvg] = useState<string>('') // svg do ícone escolhido
  const [status, setStatus] = useState<boolean>(true) // ativo/inativo
  const [priority, setPriority] = useState<string>('normal')
  const [role, setRole] = useState<string>('')
  const [visibleTo, setVisibleTo] = useState<string>('both') // visível para: employees, customers, both

  const [description, setDescription] = useState<string>('')

  // ModalWrapper (contexto)
  const {showWrapperModal} = useModals()

  // Picker de ícones
  const [icons, setIcons] = useState<any[]>([])
  const [selectedIcon, setSelectedIcon] = useState<string>('') // classe selecionada (se existir)
  const [selectedIconId, setSelectedIconId] = useState<number | null>(null) // id selecionado no modal

  // Estados para modal de grades de serviços
  const [openGridModal, setOpenGridModal] = useState(false)
  const [currentBlockId, setCurrentBlockId] = useState<string | null>(null)
  const [gridHotels, setGridHotels] = useState<GridHotel[]>([])
  const [loadingGrids, setLoadingGrids] = useState(false)
  const [gridSearchQuery, setGridSearchQuery] = useState('')
  const [selectedGridIds, setSelectedGridIds] = useState<number[]>([])
  const [isGridModalMinimized, setIsGridModalMinimized] = useState(false)

  // Estados para modal de quantidade
  const [openQuantityModal, setOpenQuantityModal] = useState(false)
  const [currentQuantityBlockId, setCurrentQuantityBlockId] = useState<string | null>(null)
  const [maxQuantity, setMaxQuantity] = useState<number>(1)

  const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 6}}))

  const addBlock = (kind: FormBlockKind) => setBlocks((prev) => [...prev, {id: uuid(), kind}])
  const removeBlock = (id: string) => setBlocks((prev) => prev.filter((b) => b.id !== id))

  // Função para abrir modal de grades de serviços
  const openGridModalForBlock = (blockId: string) => {
    setCurrentBlockId(blockId)
    setOpenGridModal(true)
    setIsGridModalMinimized(false)
    setGridSearchQuery('')

    // Carregar grades já selecionadas para este bloco
    const currentBlock = blocks.find((b) => b.id === blockId)
    setSelectedGridIds(currentBlock?.gridIds || [])

    fetchGridHotels()
  }

  // Função para buscar grades de hotel
  const fetchGridHotels = async () => {
    try {
      if (gridHotels.length !== 0) return
      setLoadingGrids(true)
      const res = await GET('/grid-hotel')
      setGridHotels(res.data || [])
    } catch (error) {
      console.error(i18n.t('service_requests.grid_services_error'), error)
      setGridHotels([])
    } finally {
      setLoadingGrids(false)
    }
  }

  // Função para togglear seleção de uma grade
  const toggleGridSelection = (gridId: number) => {
    setSelectedGridIds((prev) => {
      if (prev.includes(gridId)) {
        return prev.filter((id) => id !== gridId)
      } else {
        return [...prev, gridId]
      }
    })
  }

  // Função para confirmar seleção de grades
  const confirmGridSelection = () => {
    if (currentBlockId) {
      setBlocks((prev) =>
        prev.map((block) =>
          block.id === currentBlockId ? {...block, gridIds: selectedGridIds} : block
        )
      )
    }
    setOpenGridModal(false)
    setCurrentBlockId(null)
    setSelectedGridIds([])
    setGridSearchQuery('')
  }

  // Função para abrir modal de quantidade
  const openQuantityModalForBlock = (blockId: string) => {
    setCurrentQuantityBlockId(blockId)
    const currentBlock = blocks.find((b) => b.id === blockId)
    setMaxQuantity(currentBlock?.maxQuantity || 1)
    setOpenQuantityModal(true)
  }

  // Função para confirmar quantidade máxima
  const confirmMaxQuantity = () => {
    if (currentQuantityBlockId) {
      setBlocks((prev) =>
        prev.map((block) => (block.id === currentQuantityBlockId ? {...block, maxQuantity} : block))
      )
    }
    setOpenQuantityModal(false)
    setCurrentQuantityBlockId(null)
    setMaxQuantity(1)
  }

  const onDragEnd = ({active, over}: DragEndEvent) => {
    setActiveId(null)
    if (!over || active.id === over.id) return
    setBlocks((prev) => {
      const oldIndex = prev.findIndex((b) => b.id === active.id)
      const newIndex = prev.findIndex((b) => b.id === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const activeBlock = activeId ? blocks.find((b) => b.id === activeId) : null

  // Filtrar grades por busca
  const filteredGrids = useMemo(() => {
    if (!gridSearchQuery) return gridHotels
    const query = gridSearchQuery.toLowerCase()
    return gridHotels.filter((grid) => grid.title.toLowerCase().includes(query))
  }, [gridHotels, gridSearchQuery])

  // Abre o modal de ícones
  const openIconPicker = async () => {
    setSelectedIcon(sessionIcon) // pré-seleciona classe atual
    setSelectedIconId(sessionIconId) // pré-seleciona id atual (pra marcar ativo)
    showWrapperModal(String(i18n.t('service_requests.choose_icon')))
  }

  const ensureDescription = (html: string) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const text = doc.body.textContent?.trim() || ''

    return text === '' ? '' : html.trim()
  }

  // Carrega os dados do formulário de serviço para edição
  const loadServiceRequest = async () => {
    try {
      const res = await GET(`/room-service/${id}`)

      // A resposta pode vir diretamente ou em res.data
      const serviceRequest = res.data || res

      if (serviceRequest && serviceRequest.id) {
        // Preenche os campos básicos
        setSessionTitle(serviceRequest.title || '')
        setDescription(serviceRequest.description || '')
        setStatus(serviceRequest.status ?? true)
        setPriority(serviceRequest.priority || 'normal')
        setRole(serviceRequest.role || '')
        setVisibleTo(serviceRequest.visibleTo || 'both')
        setSessionIconId(serviceRequest.iconId || null)

        // Carrega o ícone se houver
        if (serviceRequest.iconId) {
          try {
            // Buscar todos os ícones e encontrar o correto pelo ID
            const iconsRes = await GET('/svgwebapp')
            const allIcons = iconsRes?.data || []
            const foundIcon = allIcons.find((icon: any) => icon.id === serviceRequest.iconId)

            if (foundIcon?.svg) {
              setSessionIconSvg(foundIcon.svg)
            }
          } catch (iconError) {
            // Não falha se não conseguir carregar o ícone
          }
        }

        // Reconstrói os blocos a partir dos roomServiceForms
        if (
          serviceRequest.roomServiceForms &&
          Array.isArray(serviceRequest.roomServiceForms) &&
          serviceRequest.roomServiceForms.length > 0
        ) {
          const reconstructedBlocks: Block[] = serviceRequest.roomServiceForms
            .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
            .map((form: any, index: number) => {
              const gridIds = form.roomServiceFormGrids?.map((grid: any) => grid.idGridHotel) || []

              // Verifica se o tipo é válido
              const formType = form.typeRoomService as FormBlockKind
              const validTypes: FormBlockKind[] = [
                'numberRoom',
                'date',
                'hour',
                'dateHour',
                'selectList',
                'selectCheckBox',
                'selectCount',
                'quantity',
                'selectListQuantity',
              ]

              if (!validTypes.includes(formType)) {
                return null
              }

              return {
                id: uuid(), // Novo ID para o frontend
                kind: formType,
                gridIds: gridIds.length > 0 ? gridIds : undefined,
                maxQuantity:
                  form.maxQuantity && form.maxQuantity > 1 ? form.maxQuantity : undefined,
              }
            })
            .filter(Boolean) // Remove itens null

          setBlocks(reconstructedBlocks as Block[])
        } else {
          setBlocks([])
        }
      } else {
        throw new Error('Resposta da API não contém dados válidos')
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('service_requests.error_load')),
        text: `Detalhes: ${error?.message || 'Erro desconhecido'}`,
      }).then(() => navigate('/hospitality/structured-content/service-requests'))
    }
  }

  // Função para salvar o formulário de serviço (atualização)
  const handleSave = async () => {
    try {
      setLoading(true)

      // Validações básicas
      if (!sessionTitle.trim()) {
        throw new Error(i18n.t('service_requests.validation_title'))
      }

      if (sessionIconId === null) {
        throw new Error(i18n.t('service_requests.validation_icon'))
      }

      if (blocks.length === 0) {
        throw new Error(i18n.t('service_requests.validation_components'))
      }

      if (!role) {
        throw new Error('O campo Role é obrigatório')
      }

      // Validação específica para componentes do tipo "select" - devem ter grades selecionadas
      const selectBlocksWithoutGrids = blocks.filter(
        (block) => block.kind.startsWith('select') && (!block.gridIds || block.gridIds.length === 0)
      )

      if (selectBlocksWithoutGrids.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('service_requests.validation_grids_required')),
          text: String(i18n.t('service_requests.validation_grids_text')),
          confirmButtonText: 'OK',
        })
        return
      }

      const payload = {
        title: sessionTitle.trim(),
        description: ensureDescription(description),
        iconId: sessionIconId ?? undefined,
        forms: blocks.map(({id: blockId, kind, gridIds, maxQuantity}, index) => ({
          typeRoomService: `${kind}`,
          ...(gridIds && gridIds.length > 0 && {gridHotelIds: gridIds}),
          maxQuantity: maxQuantity && maxQuantity >= 1 ? maxQuantity : 1,
          order: index,
        })),
        status,
        priority,
        role,
        visibleTo,
      }

      const res = await PUT(`/room-service/${id}`, payload)

      if (res.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('general.success')),
          text: String(i18n.t('service_requests.success_updated')),
        }).then(() => navigate('/hospitality/structured-content/service-requests'))
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('general.error')),
          text: res?.message || String(i18n.t('service_requests.error_update')),
        })
      }
    } catch (error: any) {
      console.error('Erro ao salvar formulário de serviço:', error)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('general.error')),
        text: error?.message || String(i18n.t('service_requests.error_update')),
      })
    } finally {
      setLoading(false)
    }
  }

  const iconClassOf = (it: any): string =>
    it?.class ?? it?.value ?? it?.name ?? it?.slug ?? it?.id ?? ''

  const init = async () => {
    try {
      setLoading(true)
      try {
        const iconsRes = await POST('/svgwebapp/filter')
        setIcons(iconsRes?.data ?? [])
      } catch (iconError) {
        setIcons([])
      }

      // Carrega os dados do formulário de serviço
      if (id) {
        await loadServiceRequest()
      }
    } catch (error) {
      // Erro na inicialização será tratado por loadServiceRequest
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    init()
  }, [id])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('service_requests.edit')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('service_requests.visual_instructions')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t('service_requests.add_component')}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{type: 'button'}}
                  click={() => setOpenOptionsMenu(true)}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>

            <div className='card-body'>
              {/* ===== Topo: Título e Ícone (preview) ===== */}
              <div>
                <div className='row g-3 align-items-end'>
                  <div className='col-md-6'>
                    <div className={styles.configLabel}>
                      {i18n.t('service_requests.form_title')}
                    </div>
                    <Input
                      inputAttr={{type: 'default'}}
                      label=''
                      value={sessionTitle}
                      change={(v: string) => setSessionTitle(v)}
                      placeholder={String(i18n.t('service_requests.placeholder_title'))}
                      maxCaracteres={30}
                    />
                  </div>

                  <div className='col-md-6'>
                    <div className={styles.configLabel}>{i18n.t('service_requests.form_icon')}</div>
                    <div className={styles.iconField}>
                      {/* PREVIEW DO ÍCONE SELECIONADO */}
                      <div
                        className={styles.iconPreview}
                        title={
                          sessionIconSvg
                            ? String(i18n.t('service_requests.icon_selected'))
                            : sessionIcon || String(i18n.t('service_requests.icon_none'))
                        }
                      >
                        {sessionIconSvg ? (
                          <SVG src={sessionIconSvg} style={{width: 22, height: 22}} />
                        ) : (
                          <i className={`bi ${sessionIcon || 'bi-image'}`} />
                        )}
                      </div>

                      <div className={styles.iconInputRow}>
                        <button
                          type='button'
                          className='btn btn-sm btn-light chooseBtn'
                          onClick={openIconPicker}
                        >
                          {i18n.t('service_requests.choose_icon')}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-2'>
                    <div className={styles.configLabel}>
                      {i18n.t('service_requests.form_status')}
                    </div>
                    <Input
                      inputAttr={{type: 'select'}}
                      value={status}
                      change={(val: boolean) => setStatus(val)}
                      options={[
                        {select: i18n.t(`general.active`), value: true},
                        {select: i18n.t(`general.inactive`), value: false},
                      ]}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-4'>
                    <div className={styles.configLabel}>
                      {i18n.t('service_requests.priority_label')}
                    </div>
                    <Input
                      inputAttr={{type: 'select'}}
                      value={priority}
                      change={(val: string) => setPriority(val)}
                      options={[
                        {select: i18n.t('service_requests.priority_low'), value: 'low'},
                        {select: i18n.t('service_requests.priority_normal'), value: 'normal'},
                        {select: i18n.t('service_requests.priority_high'), value: 'high'},
                        {select: i18n.t('service_requests.priority_critical'), value: 'critical'},
                      ]}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-6'>
                    <div className={styles.configLabel}>Role do Serviço</div>
                    <Input
                      inputAttr={{type: 'select'}}
                      value={role}
                      change={(val: string) => setRole(val)}
                      options={[{select: 'Nenhum', value: ''}, ...typeUser('hospitality')]}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-6'>
                    <div className={styles.configLabel}>
                      {i18n.t('service_requests.visible_to_label')}
                    </div>
                    <Input
                      inputAttr={{type: 'select'}}
                      value={visibleTo}
                      change={(val: string) => setVisibleTo(val)}
                      options={[
                        {
                          select: i18n.t('service_requests.visible_to_employees'),
                          value: 'employees',
                        },
                        {
                          select: i18n.t('service_requests.visible_to_customers'),
                          value: 'customers',
                        },
                        {select: i18n.t('service_requests.visible_to_both'), value: 'both'},
                      ]}
                      marginTop='0'
                    />
                  </div>
                  <div>
                    <div>
                      <div className={styles.configLabel}>
                        {i18n.t('service_requests.form_description')}
                      </div>
                    </div>
                    <div className='mt-6'>
                      <EditorQuill
                        change={(value: string) => {
                          setDescription(value)
                        }}
                        value={description}
                        maxLength={100}
                        height={150}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== Divisor das seções ===== */}
              <div className={styles.split}>
                <span className={styles.splitTitle}>{i18n.t('service_requests.sections')}</span>
              </div>

              {/* ===== Canvas / DnD ===== */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={(e) => setActiveId(String(e.active.id))}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={blocks.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className={styles.list}>
                    <div className='d-grid gap-3'>
                      {blocks.length === 0 ? (
                        <div className={styles.empty}>
                          {i18n.t('service_requests.empty_message')}
                        </div>
                      ) : (
                        blocks.map((b) => (
                          <SortableBlock
                            key={b.id}
                            block={b}
                            meta={metaByKind(b.kind)}
                            onRemove={removeBlock}
                            onDetails={(id) => {}}
                            onOpenGridModal={openGridModalForBlock}
                            onOpenQuantityModal={openQuantityModalForBlock}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </SortableContext>

                <DragOverlay>
                  {activeBlock ? (
                    <div className={styles.overlay} style={{width: 640, maxWidth: '90vw'}}>
                      <SortableBlock
                        block={activeBlock}
                        meta={metaByKind(activeBlock.kind)}
                        onRemove={() => {}}
                        onDetails={() => {}}
                        onOpenGridModal={() => {}}
                        onOpenQuantityModal={() => {}}
                      />
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>

            {/* Botões fixos no bottom da página */}
            <div className='card-footer border-0 pt-0 d-flex justify-content-between'>
              <Button
                text={i18n.t(`buttons.back`)}
                color='info'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={() => navigate('/hospitality/structured-content/service-requests')}
                btnClass='fw-bolder'
              />
              <Button
                text={i18n.t(`buttons.save`)}
                color='primary'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={handleSave}
                disabled={saving}
                btnClass='fw-bolder'
              />
            </div>
          </div>

          {/* Modal "Adicionar componente" (catálogo) */}
          <AddComponentModal
            isOpen={openOptionsMenu}
            onAddBlock={addBlock}
            onClose={() => setOpenOptionsMenu(false)}
          />

          {/* ModalWrapper: Picker de Ícone */}
          <IconPickerModal
            isOpen={false}
            wrapperModalOpen={true}
            icons={icons}
            selectedIconId={selectedIconId}
            selectedIcon={selectedIcon}
            setSelectedIcon={setSelectedIcon}
            setSelectedIconId={setSelectedIconId}
            onClose={() => {
              setSelectedIcon('')
              setSelectedIconId(null)
            }}
            onConfirm={() => {
              if (selectedIconId != null) {
                const chosen = icons.find((i: any) => i.id === selectedIconId)
                if (chosen?.svg) setSessionIconSvg(chosen.svg)
                setSessionIconId(selectedIconId)
              } else {
                setSessionIconSvg('')
                setSessionIconId(null)
              }
              setSessionIcon(selectedIcon || '')
            }}
          />

          {/* Modal de Grades de Serviços */}
          <GridSelectionModal
            isOpen={openGridModal}
            grids={gridHotels}
            loading={loadingGrids}
            searchQuery={gridSearchQuery}
            selectedGridIds={selectedGridIds}
            filteredGrids={filteredGrids}
            onSearchChange={setGridSearchQuery}
            onToggleGrid={toggleGridSelection}
            onClose={() => {
              setOpenGridModal(false)
              setCurrentBlockId(null)
              setSelectedGridIds([])
              setGridSearchQuery('')
            }}
            onConfirm={confirmGridSelection}
          />

          {/* Modal de Quantidade Máxima */}
          <QuantityModal
            isOpen={openQuantityModal}
            maxQuantity={maxQuantity}
            onQuantityChange={(v: string) => setMaxQuantity(parseInt(v) || 1)}
            onClose={() => {
              setOpenQuantityModal(false)
              setCurrentQuantityBlockId(null)
              setMaxQuantity(1)
            }}
            onConfirm={confirmMaxQuantity}
          />
        </div>
      )}
    </>
  )
}

const ServiceRequestsEdit: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: i18n.t('service_requests.breadcrumb_dashboard'),
            path: 'dashboard',
            isActive: false,
          },
          {
            title: i18n.t('service_requests.breadcrumb_essential_services'),
            path: 'hospitality',
            isActive: false,
          },
          {
            title: i18n.t('service_requests.title') as string,
            path: 'service-requests',
            isActive: false,
          },
          {title: i18n.t('service_requests.breadcrumb_edit'), path: 'edit', isActive: true},
        ]}
      >
        {i18n.t('service_requests.edit')}
      </PageTitle>
      <Content />
    </>
  )
}

export {ServiceRequestsEdit}
